import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon, Spinner } from "@agentsaas/ui";
import type { AgentStatus, FsEntry } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { qk } from "../data/queries";

/**
 * 文件树：按目录懒加载（fsTree depth=1），展开时才请求子目录。
 * 键盘：↑↓ 移动，→ 展开 / 进入，← 收起 / 回到父级，Enter / 空格 打开。
 * agent 容器未就绪（provisioning / unreachable / error）时不请求；进入可用状态后自动重试之前失败的目录。
 */
export function FileTree({ aid, activePath, onOpen, status }: { aid: string; activePath: string | null; onOpen(path: string): void; status?: AgentStatus }) {
  const { t } = useT();
  const qc = useQueryClient();
  const ready = isFsReady(status);
  useEffect(() => {
    if (ready) void qc.refetchQueries({ queryKey: ["fs", aid, "tree"], predicate: (q) => q.state.status === "error" });
  }, [ready, aid, qc]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const ref = useRef<HTMLUListElement>(null);
  const toggle = (p: string, open?: boolean) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (open ?? !n.has(p)) n.add(p);
      else n.delete(p);
      return n;
    });

  const onKey = (e: KeyboardEvent) => {
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="treeitem"]') ?? []);
    const cur = document.activeElement as HTMLElement | null;
    const i = cur ? items.indexOf(cur) : -1;
    if (i < 0) return;
    const path = cur!.dataset.path!;
    const isDir = cur!.dataset.type === "dir";
    const focus = (n: number) => items[Math.max(0, Math.min(items.length - 1, n))]?.focus();
    switch (e.key) {
      case "ArrowDown":
        focus(i + 1);
        break;
      case "ArrowUp":
        focus(i - 1);
        break;
      case "ArrowRight":
        if (isDir && !expanded.has(path)) toggle(path, true);
        else focus(i + 1);
        break;
      case "ArrowLeft":
        if (isDir && expanded.has(path)) toggle(path, false);
        else {
          const parent = path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : null;
          if (parent) items.find((x) => x.dataset.path === parent)?.focus();
        }
        break;
      case "Home":
        focus(0);
        break;
      case "End":
        focus(items.length - 1);
        break;
      case "Enter":
      case " ":
        if (isDir) toggle(path);
        else onOpen(path);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  return (
    <ul ref={ref} className="tree" role="tree" aria-label={t("fs.tree")} onKeyDown={onKey}>
      {ready ? (
        <Dir aid={aid} path="" depth={0} expanded={expanded} toggle={toggle} activePath={activePath} onOpen={onOpen} />
      ) : (
        <li role="none" className="tree__status muted" data-testid="tree-waiting">
          <Spinner label={t("fs.waiting_agent")} /> <span aria-hidden="true">{t("fs.waiting_agent")}</span>
        </li>
      )}
    </ul>
  );
}

/** 文件系统可访问的 agent 状态（容器在跑） */
export function isFsReady(status: AgentStatus | undefined): boolean {
  return status === undefined || !(status === "provisioning" || status === "unreachable" || status === "error");
}

function Dir({ aid, path, depth, expanded, toggle, activePath, onOpen }: { aid: string; path: string; depth: number; expanded: Set<string>; toggle(p: string): void; activePath: string | null; onOpen(p: string): void }) {
  const { t } = useT();
  const errText = useErrorText();
  const { api } = useServices();
  const q = useQuery({
    queryKey: qk.tree(aid, path),
    queryFn: async () => (await api.call("fsTree", { params: { aid }, query: { path, depth: 1 } })).entries,
  });
  if (q.isPending)
    return (
      <li role="none" className="tree__status" style={{ paddingLeft: 12 + depth * 14 }}>
        <Spinner label={t("common.loading")} />
      </li>
    );
  if (q.isError)
    return (
      <li role="none" className="tree__status tree__error" style={{ paddingLeft: 12 + depth * 14 }}>
        {errText(q.error)}
      </li>
    );
  if (!q.data.length)
    return (
      <li role="none" className="tree__status muted" style={{ paddingLeft: 12 + depth * 14 }}>
        {t("fs.empty")}
      </li>
    );
  return (
    <>
      {q.data.map((e, idx) => (
        <Node key={e.path} aid={aid} entry={e} depth={depth} expanded={expanded} toggle={toggle} activePath={activePath} onOpen={onOpen} first={depth === 0 && idx === 0} />
      ))}
    </>
  );
}

function Node({ aid, entry, depth, expanded, toggle, activePath, onOpen, first }: { aid: string; entry: FsEntry; depth: number; expanded: Set<string>; toggle(p: string): void; activePath: string | null; onOpen(p: string): void; first: boolean }) {
  const isDir = entry.type === "dir";
  const open = isDir && expanded.has(entry.path);
  const active = activePath === entry.path;
  return (
    <li role="none">
      <div
        role="treeitem"
        aria-expanded={isDir ? open : undefined}
        aria-selected={active}
        aria-level={depth + 1}
        tabIndex={active || (activePath === null && first) ? 0 : -1}
        data-path={entry.path}
        data-type={entry.type}
        className={`tree__item${active ? " is-active" : ""}`}
        style={{ paddingLeft: 8 + depth * 14 }}
        onClick={() => (isDir ? toggle(entry.path) : onOpen(entry.path))}
      >
        <Icon name={isDir ? (open ? "chevronDown" : "chevronRight") : "file"} size={14} />
        <span className="ellipsis">{entry.name}</span>
      </div>
      {open ? (
        <ul role="group">
          <Dir aid={aid} path={entry.path} depth={depth + 1} expanded={expanded} toggle={toggle} activePath={activePath} onOpen={onOpen} />
        </ul>
      ) : null}
    </li>
  );
}
